/* eslint-disable no-console */
import axios, { AxiosRequestConfig, CancelTokenSource } from 'axios';
import qs from 'qs';
import {
  CAROUSELS,
  COLLECTIONS_URL,
  COLLECTION_DETAILS_URL,
  TRANSACTIONS_URL,
  TRANSACTION_FOR_COLLECTION_URL,
  USER_ADDRESSES,
  USER_GAMES,
  WALLET_BALANCE,
  WITHDRAW_VERIFY_URL,
  WITHDRAW_URL,
  CARDS_METADATA_URL,
  DEPOSIT_URL,
  NEWSLETTER_URL,
} from '../consts';
import { CarouselItem } from '../models/carousel-item-model';
import { CollectionDetails } from '../models/collection-details-model';
import Collections from '../models/collections-model';
import { DepositExecutorResponse } from '../models/deposit-executor-response-model';
import NewsletterResponse from '../models/newsletter-response-model';
import { OrbDetails } from '../models/orb-details-model';
import { Transactions } from '../models/transactions-model';
import { UserGames } from '../models/user-games-model';
import { User } from '../models/user-model';
import { WalletBalance } from '../models/wallet-balance-model';
import WithdrawResponse from '../models/withdraw-response-model';

export default class ApiService {
  public getCollectionsAsync = async (): Promise<Collections> => {
    try {
      const response = await axios.get(COLLECTIONS_URL);
      return response.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  };

  public getTransactionsAsync = async (collectionName?: string, address?: string): Promise<Transactions> => {
    let url = collectionName ? TRANSACTION_FOR_COLLECTION_URL + collectionName : TRANSACTIONS_URL;
    url = url.replace('{address}', address ? `/${address}` : '');
    try {
      const response = await axios.get(url);
      const result: Transactions = response.data;
      return result;
    } catch (e) {
      console.error(e);
      throw e;
    }
  };

  public getCollectionDetailsAsync = async (collectionName: string): Promise<CollectionDetails> => {
    try {
      const response = await axios.get(COLLECTION_DETAILS_URL + collectionName);

      // Fix for the API inconsistency
      if (!response.data) {
        return {} as CollectionDetails;
      }

      // filtering the collection from the api from incorrect items
      // TODO: remove that logic when it will be resolved on the server
      // TODO: the task to remove incorrect data on the server - ORBEXP-489
      const incorrectImageUrl =
        'https://metadata.spellsofgenesis.com/0x9227a3d959654c8004fa77dffc380ec40880fff6/image/0.jpg';

      const result = response.data;
      const keys = Object.keys(result.data.assets);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const fixedAssets: any = {};
      keys.forEach((assetId) => {
        if (result.data.assets[assetId].imageUrl !== incorrectImageUrl) {
          fixedAssets[assetId] = result.data.assets[assetId];
          if (fixedAssets[assetId].contract) {
            if (fixedAssets[assetId].contracts) {
              throw new Error('Incorrect API data!');
            }

            fixedAssets[assetId].contracts = [result.data.assets[assetId].contract];
          }
        }
      });

      result.data.assets = fixedAssets;

      return result;
    } catch (e) {
      console.error(e);
      throw e;
    }
  };

  public getUserAddressesAsync = async (address: string): Promise<User> => {
    try {
      const response = await axios.get(USER_ADDRESSES + address);
      return response.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  };

  public getWalletBalanceAsync = async (address: string): Promise<WalletBalance> => {
    try {
      const response = await axios.get(WALLET_BALANCE + address);
      return response.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  };

  public getUserAppsAsync = async (address: string, gameName: string): Promise<UserGames> => {
    try {
      const url = USER_GAMES.replace('{address}', address ? `/${address}` : '') + gameName;
      const response = await axios.get(url);
      return response.data;
    } catch (e) {
      console.warn(e);
      return {} as UserGames;
    }
  };

  public getCarouselsAsync = async (): Promise<CarouselItem[]> => {
    try {
      const response = await axios.get(CAROUSELS);
      return response.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  };

  public postWithdrawVerify = async (
    address: string,
    asset: string,
    blockchain: string,
    signerAddress: string,
    quantity: number,
    source: CancelTokenSource
  ): Promise<WithdrawResponse> => {
    return axios
      .post(
        WITHDRAW_VERIFY_URL,
        {
          address,
          asset,
          blockchain,
          signerAddress,
          quantity,
        },
        {
          cancelToken: source.token,
        }
      )
      .then((response) => {
        return Promise.resolve(response.data);
      })
      .catch((error) => {
        console.error('error', error);
        return Promise.reject(error);
      });
  };

  public postWithdraw = async (
    address: string,
    asset: string,
    blockchain: string,
    signature: string,
    signerAddress: string,
    toAddress: string,
    quantity: number,
    source: CancelTokenSource
  ): Promise<WithdrawResponse> => {
    return axios
      .post(
        WITHDRAW_URL,
        {
          address,
          asset,
          blockchain,
          signature,
          signerAddress,
          toAddress,
          quantity,
        },
        {
          cancelToken: source.token,
        }
      )
      .then((response) => {
        return Promise.resolve(response.data);
      })
      .catch((error) => {
        console.error('error', error);
        return Promise.reject(error);
      });
  };

  public getDepositAddresses = async (
    counterpartyAddress: string,
    source: CancelTokenSource
  ): Promise<DepositExecutorResponse> => {
    return axios
      .get(DEPOSIT_URL + counterpartyAddress, {
        cancelToken: source.token,
      })
      .then((response) => {
        return Promise.resolve(response.data);
      })
      .catch((error) => {
        console.error('error', error);
        return Promise.reject(error);
      });
  };

  public getCardMetadataAsync = async (cardId: string): Promise<OrbDetails> => {
    try {
      const url = `${CARDS_METADATA_URL}?token=${cardId}`;
      const response = await axios.get(url);
      return response.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  };

  public sendNewsletterSignUp = async (email: string): Promise<NewsletterResponse> => {
    try {
      const url = NEWSLETTER_URL;
      const data = qs.stringify({ EMAIL: email, email_address_check: '', locale: 'en' });
      const response = await axios.post(`${url}?isAjax=1`, data, ApiService.getConfigUrlEncoded());
      return response.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  };

  private static getConfigUrlEncoded(): AxiosRequestConfig {
    return {
      headers: {
        accept: '*/*',
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    };
  }
}
